/**
 * 🔥 Phoenix Rush — boss gauntlet
 *
 * Rules (Roc canon 2026-10-03):
 *   • 1–4 heroes vs. a boss ladder (currently 3 filed: Gorgonax, Ash-
 *     Winged Kensei, Laughlin Elephant). Each boss +10 levels.
 *   • Next boss arrives IMMEDIATELY on kill. NO full heal between.
 *   • 1 Rush Box per kill (4-5 drops + 1 Phoenix Revival), on the spot.
 *
 * v1 UI: round-by-round "Fight Round" button, HP bars for boss & party,
 * live log, reveal modal for the Rush Box. Discovery-first — the catalog
 * shows locked bosses as "???" until the Rush is in session.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Swords, Shield, Lock, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Boss catalog card ──────────────────────────────────────
function BossCatalogCard({ b, cleared, active }) {
  // Discovery-first: Boss #1 is revealed by default (so new players
  // know what's at the door). Everything else stays "???" until the
  // player actually reaches it.
  const locked = !active && !cleared && b.order !== 1;
  return (
    <div
      className={`relative rounded-xl border-2 p-3 ${
        active
          ? "border-amber-400"
          : cleared
            ? "border-emerald-500/50"
            : locked
              ? "border-neutral-800 opacity-60"
              : "border-neutral-700"
      }`}
      style={{
        background: `linear-gradient(135deg, ${locked ? "#2222" : (b.theme?.primary || "#333")}22, transparent 60%)`,
        boxShadow: active ? `0 0 32px ${b.theme?.primary || "#F59E0B"}66` : "none",
      }}
      data-testid={`rush-boss-card-${b.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-black/60 text-neutral-300">
          Boss #{b.order}{locked ? "" : ` · Lv${b.level}`}
        </span>
        {cleared && <span className="text-emerald-400 text-xs font-bold">CLEARED</span>}
        {locked && <Lock size={12} className="text-neutral-600" />}
      </div>
      <div className="text-center my-2">
        <div className="text-5xl" style={{ filter: locked ? "grayscale(1)" : "none" }}>
          {locked ? "❓" : b.icon}
        </div>
        <div className="text-sm font-black text-white mt-1 uppercase tracking-wider">
          {locked ? "???" : b.name}
        </div>
      </div>
    </div>
  );
}

// ── Loot reveal modal ──────────────────────────────────────
// 🎁 Roc rule 2026-10-04 — "no cinematics. ONE item on ONE line, like
// the guild lever results." Never split into multiple lines.
function RushBoxOpenedModal({ payload, onClose }) {
  if (!payload) return null;
  const { boss_name, contents } = payload;
  const drop = contents?.drop || {};
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="rush-box-opened-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl border-2 border-amber-500/70 bg-gradient-to-br from-amber-950/40 via-black to-neutral-950 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 mb-1">
            Rush Box · {boss_name || "Unknown"}
          </div>
          <div className="text-xl font-black text-white uppercase" style={{ fontFamily: "Anton, sans-serif" }}>
            Opened
          </div>
        </div>

        <div data-testid="rush-box-opened-contents">
          {drop.key && (
            <div
              className="flex items-center gap-3 p-3 rounded bg-black/50 border border-amber-700/40"
              data-testid={`rush-box-drop-${drop.key}`}
            >
              <span className="text-2xl">{drop.icon || "🎁"}</span>
              <div className="flex-1 min-w-0 text-sm font-bold text-amber-100">
                {drop.label || drop.key}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="rush-box-opened-close"
          className="mt-4 w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase tracking-widest"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── DEPRECATED (Roc rule 2026-10-04 replaced the mid-run modal) ───
function RushLootModal({ loot, onClose }) {
  if (!loot) return null;
  const uniques = loot.unique_items || [];
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="rush-loot-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-md w-full rounded-2xl border-2 border-amber-400/70 bg-gradient-to-br from-amber-950/60 via-black to-neutral-950 p-6"
        style={{ boxShadow: "0 0 60px rgba(251,191,36,0.4)" }}
      >
        <button
          onClick={onClose}
          data-testid="rush-loot-close"
          className="absolute top-3 right-3 text-neutral-400 hover:text-white"
        >
          <X size={20} />
        </button>
        <div className="text-center mb-4">
          <div className="text-5xl mb-2">🎁</div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300">
            Rush Box
          </div>
          <div className="text-xl font-black text-white uppercase">{loot.label}</div>
        </div>
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {(loot.rewards || []).map((r) => (
            <div
              key={r.key}
              className="flex items-center gap-2 text-sm bg-black/40 rounded px-2 py-1"
              data-testid={`rush-reward-${r.key}`}
            >
              <span className="text-base">{r.icon}</span>
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 truncate">
                  {r.name}
                </div>
                <div className="text-sm font-bold text-amber-200">+{Number(r.amount).toLocaleString()}</div>
              </div>
            </div>
          ))}
          <div
            className="flex items-center gap-2 text-sm bg-black/40 rounded px-2 py-1 col-span-2"
            data-testid="rush-reward-revival"
          >
            <span className="text-base">🐦‍🔥</span>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-neutral-500">Phoenix Revival</div>
              <div className="text-sm font-bold text-amber-200">+{loot.revival || 1}</div>
            </div>
          </div>
        </div>
        {uniques.length > 0 && (
          <div className="rounded-lg border-2 border-amber-400/50 bg-gradient-to-br from-amber-900/40 to-orange-900/30 p-3 mb-3" data-testid="rush-uniques">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">
              Unique Drops
            </div>
            {uniques.map((u) => (
              <div
                key={u.key}
                className="flex items-start gap-2 mb-2 last:mb-0"
                data-testid={`rush-unique-${u.key}`}
              >
                <span className="text-2xl">{u.icon}</span>
                <div className="flex-1">
                  <div className="text-sm font-black text-amber-100">{u.name}</div>
                  <div className="text-[11px] text-neutral-300 italic">
                    {u.passive || u.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={onClose}
          data-testid="rush-loot-ok"
          className="w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase tracking-widest"
        >
          Collect
        </button>
      </div>
    </div>
  );
}

// 💸 Rush Skill Shop — buyable charges in the prep room (owner canon
// 2026-10-05). 50 OD each for Flame Rage/Fortress/Respite (max 3 extra),
// 75 OD for Phoenix Revival (max 1 extra).
// 🔒 Feb 2026 — shop LOCKS the moment the first fight round fires. Owner
// directive: no mid-fight panic buying. The lock surfaces as a 🔒 overlay
// on each tile + a disabled Buy button + a "Shop locked" caption. The
// server also rejects buys with error `shop_locked_combat_started`.
function RushSkillShop({ session, myUserId, onBuy, isPrep }) {
  const skillsForMe = session?.rush_skills_by_user?.[myUserId]
                        || session?.rush_skills
                        || null;
  if (!skillsForMe) return null;
  const locked = !!session?.shop_locked;
  return (
    <section
      className={`rounded-xl border p-3 ${locked
        ? "border-neutral-700/50 bg-gradient-to-br from-neutral-900 via-black to-neutral-950"
        : "border-amber-700/40 bg-gradient-to-br from-amber-950/40 via-black to-neutral-950"
      }`}
      data-testid="rush-skill-shop"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className={`text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${locked ? "text-neutral-400" : "text-amber-200"}`}>
          {locked ? <span>🔒</span> : <span>💸</span>}
          {locked
            ? "Rush Kit Shop · Locked"
            : (isPrep ? "Prep Room · Rush Kit Shop" : "Rush Kit Shop")}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-neutral-500">
          {locked
            ? "Fight has started — shop is sealed for this run"
            : "Flame/Fortress/Respite · 50 OD · max 3 extra · Revival 75 OD · max 1 extra"}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(skillsForMe.skills || []).map((s) => {
          const soldOut = !s.can_buy;
          const disabled = locked || soldOut;
          return (
            <div
              key={s.id}
              className={`p-2 rounded border relative ${locked
                ? "border-neutral-800 bg-neutral-950/70"
                : "border-amber-800/40 bg-black/40"
              }`}
              data-testid={`rush-shop-${s.id}`}
            >
              {locked && (
                <div
                  className="absolute inset-0 flex items-center justify-center rounded bg-black/50 backdrop-blur-[1px]"
                  data-testid={`rush-shop-lock-${s.id}`}
                  aria-hidden="true"
                >
                  <span className="text-2xl opacity-80">🔒</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-1">
                <span className={`text-lg leading-none ${locked ? "opacity-40" : ""}`}>{s.icon}</span>
                <span className={`text-[9px] font-mono tabular-nums ${locked ? "text-neutral-500" : "text-amber-200"}`}>
                  Have ×{s.charges}
                </span>
              </div>
              <div className={`text-[10px] font-bold uppercase tracking-widest mt-1 truncate ${locked ? "text-neutral-400" : "text-amber-100"}`}>
                {s.name}
              </div>
              <div className="text-[9px] text-neutral-500 leading-tight truncate" title={s.flavor}>
                {s.flavor}
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && onBuy(s.id)}
                data-testid={`rush-shop-buy-${s.id}`}
                className={`mt-1.5 w-full text-[10px] uppercase tracking-widest py-1 rounded font-black ${
                  disabled
                    ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                    : "bg-amber-500 text-black hover:bg-amber-400"
                }`}
              >
                {locked ? "🔒 Locked" : (soldOut ? `Max ${s.max_purchase}` : `Buy +1 · ${s.od_cost} OD`)}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}


// ⚡ Rush Special Skills bar
// Each party member gets 3 charges of Flame Rage / Fortress / Respite +
// 1 Phoenix Revival. 60s cooldown clock per skill after use.
function RushSkillsBar({ session, myUserId, onUse }) {
  const skillsForMe = (session?.rush_skills_by_user?.[myUserId]
                        || session?.rush_skills
                        || null);
  if (!skillsForMe) return null;
  const buffs = {
    flame_rage: Number(session?.flame_rage_turns || 0),
    fortress:   Number(session?.fortress_turns   || 0),
    revival_banked: !!session?.revival_banked,
  };
  return (
    <section
      className="rounded-xl border border-amber-900/40 bg-gradient-to-br from-orange-950/40 via-black to-neutral-950 p-3"
      data-testid="rush-skills-bar"
    >
      <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold mb-2">
        ⚡ Rush Special Kit
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(skillsForMe.skills || []).map((s) => {
          const onCd = Number(s.cooldown_s || 0) > 0;
          const noCharges = Number(s.charges || 0) <= 0;
          const disabled = onCd || noCharges;
          const activeTurns = s.id === "flame_rage" ? buffs.flame_rage
                              : s.id === "fortress"  ? buffs.fortress
                              : 0;
          const banked = s.id === "phoenix_revival" && buffs.revival_banked;
          return (
            <button
              key={s.id}
              type="button"
              disabled={disabled}
              onClick={() => onUse(s.id)}
              data-testid={`rush-skill-${s.id}`}
              className={`relative text-left p-2 rounded border transition
                ${disabled
                  ? "bg-neutral-900 border-neutral-800 text-neutral-600 cursor-not-allowed"
                  : "bg-amber-950/40 border-amber-500/50 text-amber-100 hover:bg-amber-900/40"}`}
              title={s.flavor}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-base leading-none">{s.icon}</span>
                <span className="text-[9px] font-mono tabular-nums"
                      data-testid={`rush-skill-${s.id}-charges`}>
                  ×{s.charges}
                </span>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest mt-1 truncate">
                {s.name}
              </div>
              <div className="text-[9px] text-neutral-500 leading-tight truncate">
                {onCd ? `${s.cooldown_s}s` : (activeTurns > 0 ? `${activeTurns} turns` : (banked ? "Banked" : "Ready"))}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}


// 🩹 Feb 2026 — Per-hero HP cards during Rush combat. Reads the real
// per-hero `combatants` list from the backend (one HP bar per hero,
// per-hero damage distribution, per-hero attacks). Falls back to the
// legacy projected-share view only if the server hasn't shipped the
// combatants field yet (lazy-migration path for in-flight sessions).
function PartyHpCards({ sess }) {
  const combatants = Array.isArray(sess?.combatants) ? sess.combatants : [];
  if (combatants.length > 0) {
    return (
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="rush-party-hp-cards">
        {combatants.map((c, i) => {
          const max  = Math.max(1, Number(c.max_hp || 1));
          const cur  = Math.max(0, Number(c.hp || 0));
          const pct  = Math.max(0, Math.min(100, Math.round((cur / max) * 100)));
          const dead = !c.alive || cur <= 0;
          return (
            <div
              key={c.user_id || i}
              className={`rounded-lg p-2 border ${dead ? "border-neutral-800 bg-neutral-900/70 opacity-50" : "border-emerald-900/40 bg-black/50"}`}
              data-testid={`rush-party-hp-card-${i}`}
            >
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="font-bold text-emerald-100 truncate flex items-center gap-1">
                  {c.is_host && <span title="Host">👑</span>}
                  {c.handle || `Hero ${i+1}`}
                  {c.guild_buffs_applied && <span title="Guild shards active" className="text-amber-300">🛡️</span>}
                </span>
                <span className="font-mono text-emerald-300 tabular-nums">
                  {dead ? "DOWN" : `${cur}/${max}`}
                </span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-black/60 border border-emerald-900/40 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    pct > 50 ? "bg-gradient-to-r from-emerald-700 to-emerald-400" :
                    pct > 20 ? "bg-gradient-to-r from-amber-700 to-amber-400" :
                    "bg-gradient-to-r from-red-800 to-red-500"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1 text-[9px] text-emerald-400/60 font-mono tabular-nums">
                ⚔️ {c.power || 0}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  // Legacy fallback (party_hp pool only — no per-hero data available).
  const party = Array.isArray(sess?.party) ? sess.party : [];
  if (party.length === 0) return null;
  const total = Math.max(1, Number(sess?.party_hp_max || 1));
  const cur   = Math.max(0, Number(sess?.party_hp || 0));
  const share = Math.floor(total / party.length);
  const pct   = Math.max(0, Math.min(100, Math.round((cur / total) * 100)));
  const shareCur = Math.round(share * (pct / 100));
  return (
    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="rush-party-hp-cards">
      {party.map((h, i) => (
        <div
          key={h.user_id || i}
          className="rounded-lg p-2 border border-emerald-900/40 bg-black/50"
          data-testid={`rush-party-hp-card-${i}`}
        >
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="font-bold text-emerald-100 truncate">{h.hero_nameplate || h.name || `Hero ${i+1}`}</span>
            <span className="font-mono text-emerald-300 tabular-nums">{shareCur}/{share}</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-black/60 border border-emerald-900/40 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                pct > 50 ? "bg-gradient-to-r from-emerald-700 to-emerald-400" :
                pct > 20 ? "bg-gradient-to-r from-amber-700 to-amber-400" :
                "bg-gradient-to-r from-red-800 to-red-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}


// ── HP bar — exact numbers (in-fight is learning time) ────
function HPBar({ cur, max, color = "#DC2626", label }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (cur / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold mb-0.5">
        <span className="text-neutral-300">{label}</span>
        <span className="tabular-nums text-neutral-400">{Math.max(0, cur).toLocaleString()} / {max.toLocaleString()}</span>
      </div>
      <div className="h-3 w-full rounded-full bg-neutral-900 overflow-hidden border border-neutral-800">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function PhoenixRushPage() {
  const { user } = useAuth();
  const myUserId = user?.user_id;
  const [state, setState]     = useState(null);
  const [fighting, setFighting] = useState(false);
  const [starting, setStarting] = useState(false);
  // 🔥 Oct 2026 — helper reward opt-in (checkbox). Default: unchecked.
  const [wantsRewards, setWantsRewards] = useState(false);
  const [lobby, setLobby]       = useState([]);
  // 🎁 Oct 2026 — Unopened Rush Boxes panel. Boxes are awarded at
  // run-end (win or wipe) and stay sealed in the user's inventory
  // until they tap Open — same model as CCG packs.
  const [boxes, setBoxes]       = useState({ sealed: [], opened: [], sealed_count: 0 });
  const [openingBoxId, setOpeningBoxId] = useState(null);
  const [lastOpened, setLastOpened]     = useState(null);  // {box_name, contents}
  const logBottomRef            = useRef(null);
  const heartbeatRef            = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/phoenix-rush/state`, { withCredentials: true });
      setState(data);
    } catch (e) {
      toast.error("Failed to load Phoenix Rush.");
    }
  };

  const loadLobby = async () => {
    try {
      const { data } = await axios.get(`${API}/phoenix-rush/rooms`, { withCredentials: true });
      setLobby(data?.rooms || []);
    } catch { /* silent */ }
  };

  const loadBoxes = async () => {
    try {
      const { data } = await axios.get(`${API}/phoenix-rush/boxes`, { withCredentials: true });
      setBoxes(data || { sealed: [], opened: [], sealed_count: 0 });
    } catch { /* silent — boxes are optional */ }
  };

  const openBox = async (box_id) => {
    if (openingBoxId) return;
    setOpeningBoxId(box_id);
    try {
      const { data } = await axios.post(
        `${API}/phoenix-rush/boxes/${box_id}/open`,
        {}, { withCredentials: true },
      );
      if (data?.ok) {
        setLastOpened({
          boss_name: data.boss_name,
          contents:  data.contents,
        });
        await loadBoxes();
      } else {
        toast.error(data?.error || "Could not open box.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open box.");
    } finally {
      setOpeningBoxId(null);
    }
  };

  useEffect(() => { load(); loadLobby(); loadBoxes(); }, []);

  // 💓 Heartbeat loop + session poll for live multiplayer state.
  const sessionId = state?.session?.session_id;
  const isHost    = !!state?.is_host;
  const isHelper  = !!state?.is_helper;
  useEffect(() => {
    if (!sessionId || !(isHost || isHelper)) {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
      return;
    }
    heartbeatRef.current = setInterval(async () => {
      try {
        if (isHost) {
          await axios.post(`${API}/phoenix-rush/room/${sessionId}/heartbeat`, {}, { withCredentials: true });
        }
        await load();
      } catch { /* keep rolling */ }
    }, 45000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [sessionId, isHost, isHelper]);

  // Auto-scroll log on new entries.
  useEffect(() => {
    if (logBottomRef.current) {
      logBottomRef.current.scrollTop = logBottomRef.current.scrollHeight;
    }
  }, [state?.session?.log?.length]);

  const startSolo = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/phoenix-rush/start`, {}, { withCredentials: true });
      if (data.ok) {
        toast.success(data.resumed ? "Resumed your Rush." : "Flying solo. Good luck.");
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Rush.");
    } finally {
      setStarting(false);
    }
  };

  const createRoom = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/phoenix-rush/room/create`, {}, { withCredentials: true });
      if (data.ok) {
        toast.success(data.resumed ? "Resumed your room." : "Rush room open — invite helpers.");
        await load();
        await loadLobby();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open room.");
    } finally {
      setStarting(false);
    }
  };

  const joinRoom = async (sid) => {
    try {
      const { data } = await axios.post(`${API}/phoenix-rush/room/${sid}/join`, {}, { withCredentials: true });
      if (data.ok) {
        toast.success("Joined the room — stand by for the host.");
        await load();
      } else {
        toast.error(data.error || "Could not join.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Join failed.");
    }
  };

  const leaveRoom = async () => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/phoenix-rush/room/${sessionId}/leave`, {}, { withCredentials: true });
      await load();
      await loadLobby();
      toast.message("Left the room.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Leave failed.");
    }
  };

  const kickHelper = async (targetId) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/phoenix-rush/room/${sessionId}/kick`,
        { user_id: targetId }, { withCredentials: true });
      await load();
    } catch { /* silent */ }
  };

  // 🔥 Roc rule 2026-10 — helper opts into rewards via checkbox.
  // Unchecked (default) = pure helping, no payout. Rotate freely.
  const toggleRewards = async (checked) => {
    if (!sessionId) return;
    setWantsRewards(checked);                       // optimistic UI
    try {
      const { data } = await axios.post(
        `${API}/phoenix-rush/room/${sessionId}/rewards`,
        { wants_rewards: !!checked },
        { withCredentials: true },
      );
      if (data?.ok) await load();
      else if (data?.error) {
        toast.error(data.error);
        setWantsRewards(!checked);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update reward opt-in.");
      setWantsRewards(!checked);
    }
  };

  const fight = async () => {
    if (fighting) return;
    // Helpers can't fire — only the host and solo runners.
    if (isHelper) {
      toast.error("The host fires the fight.");
      return;
    }
    setFighting(true);
    try {
      const url = isHost
        ? `${API}/phoenix-rush/room/${sessionId}/fight`
        : `${API}/phoenix-rush/fight`;
      const { data } = await axios.post(url, {}, { withCredentials: true });
      // 🪂 Roc rule 2026-10-04 — seamless continuum. Mid-run kills NEVER
      // open a reward surface. Boxes are only awarded at run-end (win or
      // wipe), and even then they land silently in the Unopened panel —
      // no HUD flash, no toast, no counter. The player will find them
      // waiting when they look.
      await load();
      await loadBoxes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fight failed.");
    } finally {
      setFighting(false);
    }
  };

  // 🚫 Roc rule 2026-10-04 — Phoenix Rush is WIPE-OR-GLORY. There is
  // no abandon handler. The run ends only on party wipe or final-boss
  // cleared. (A helper can still leave the room — kept intact below.)

  const toggleHostRewards = async (checked) => {
    if (!sessionId) return;
    try {
      await axios.post(
        `${API}/phoenix-rush/room/${sessionId}/host-rewards`,
        { wants_rewards: !!checked },
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update your opt-in.");
    }
  };

  const useRushSkill = async (skillId) => {
    try {
      const url = isHost || !sessionId
        ? `${API}/phoenix-rush/use-skill`
        : `${API}/phoenix-rush/room/${sessionId}/use-skill`;
      const { data } = await axios.post(url, { skill_id: skillId }, { withCredentials: true });
      if (!data?.ok) {
        if (data?.error === "on_cooldown") {
          toast.message(`On cooldown — ${data.cooldown_s}s left.`);
        } else if (data?.error === "no_charges") {
          toast.message("Out of charges this run.");
        } else {
          toast.error(data?.error || "Skill failed.");
        }
        return;
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Skill failed.");
    }
  };

  const buyRushSkillCharge = async (skillId) => {
    try {
      const url = isHost || !sessionId
        ? `${API}/phoenix-rush/buy-skill-charge`
        : `${API}/phoenix-rush/room/${sessionId}/buy-skill-charge`;
      const { data } = await axios.post(url, { skill_id: skillId }, { withCredentials: true });
      if (!data?.ok) {
        if (data?.error === "insufficient_od") {
          toast.message(`Need ${data.needed} OD — top up your Dragonious wallet first.`);
        } else if (data?.error === "max_purchases_reached") {
          toast.message("Already maxed on this skill for this run.");
        } else {
          toast.error(data?.error || "Shop refused the purchase.");
        }
        return;
      }
      toast.success("+1 charge banked.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shop refused the purchase.");
    }
  };

  // Keep local `wantsRewards` in sync with the server-held flag whenever
  // the session doc refreshes.
  useEffect(() => {
    const sess = state?.session || {};
    if (!myUserId) return;
    // Host sees host_wants_rewards; helpers see their own.
    if (sess.host_user_id === myUserId) {
      setWantsRewards(!!sess.host_wants_rewards);
      return;
    }
    const me = (sess.helpers || []).find((h) => h.user_id === myUserId);
    if (me) setWantsRewards(!!me.wants_rewards);
  }, [state?.session, myUserId]);

  if (!state) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-500" data-testid="rush-loading">
        Loading Phoenix Rush…
      </div>
    );
  }

  const sess        = state.session || {};
  const catalog     = state.catalog || [];
  const inSession   = !!sess.session_id;
  const boss        = sess.boss;
  const clearedIds  = new Set(sess.cleared_bosses || []);
  const sessStatus  = sess.status;
  // 💸 Prep room = session alive, no boss attacked yet. Starts the moment
  // a run is created and ends on the first fight round. During prep the
  // UI foregrounds the skill shop and relabels the Fight button.
  const isPrep      = sessStatus === "active" &&
                      Number(sess.round || 0) === 0 &&
                      (sess.cleared_bosses || []).length === 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 pb-24" data-testid="phoenix-rush-root">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-amber-900/40">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            background: "radial-gradient(circle at 30% 30%, rgba(251,146,60,0.4), transparent 50%), radial-gradient(circle at 75% 75%, rgba(220,38,38,0.3), transparent 55%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 py-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-amber-500/60 rounded-full mb-3">
            <Flame size={14} className="text-amber-300" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-200">Phoenix Rush</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-[0.9] mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
            STEP THROUGH <span className="text-amber-400">THE DOOR.</span>
          </h1>
          <div className="mt-3">
            <Link to="/arena" className="text-[10px] uppercase tracking-widest text-amber-300/70 hover:text-amber-200" data-testid="rush-back-arena">
              ← Back to Arena
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">

        {/* 🎁 Unopened Rush Boxes — Roc rule 2026-10-04. Sealed boxes land
            here silently at run-end (win or wipe), 1 per boss defeated.
            No HUD flash, no toast; the player finds them waiting. */}
        {boxes.sealed_count > 0 && (
          <section
            className="rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-950/40 via-black to-neutral-950 p-4"
            data-testid="rush-unopened-boxes"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎁</span>
                <div>
                  <h2 className="text-[11px] uppercase tracking-widest font-bold text-amber-200">
                    Unopened Rush Boxes
                  </h2>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400/60">
                    Sealed · {boxes.sealed_count} waiting
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-1.5" data-testid="rush-unopened-list">
              {(boxes.sealed || []).slice(0, 20).map((b) => (
                <div
                  key={b.box_id}
                  className="flex items-center justify-between gap-3 p-2.5 rounded border border-amber-700/40 bg-black/40"
                  data-testid={`rush-box-${b.box_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xl">📦</span>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-amber-100 truncate">
                        {b.boss_name || "Rush Box"}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                        Sealed · {(b.earned_at || "").slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openBox(b.box_id)}
                    disabled={!!openingBoxId}
                    data-testid={`rush-box-open-${b.box_id}`}
                    className="px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-widest bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black"
                  >
                    {openingBoxId === b.box_id ? "Opening…" : "Open"}
                  </button>
                </div>
              ))}
              {boxes.sealed.length > 20 && (
                <div className="text-[10px] text-neutral-500 italic text-center pt-1">
                  …and {boxes.sealed.length - 20} more
                </div>
              )}
            </div>
          </section>
        )}

        {/* Boss catalog — only shown DURING a run so pre-session
            players don't see the ladder structure. */}
        {inSession && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Swords size={14} className="text-amber-300" />
              <h2 className="text-[11px] uppercase tracking-widest font-bold text-amber-200">Boss Ladder</h2>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="rush-catalog">
              {catalog.map((b) => (
                <BossCatalogCard
                  key={b.id}
                  b={b}
                  cleared={clearedIds.has(b.id)}
                  active={boss?.id === b.id}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Fight console / Start CTA ── */}
        {!inSession ? (
          <section
            className="rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-950/40 via-black to-red-950/30 p-6"
            data-testid="rush-start-panel"
          >
            <div className="text-center text-xs italic text-neutral-500 mb-5">
              Make a room. Invite who you want. If nobody joins, you fly solo.
            </div>
            <div className="grid grid-cols-1 gap-3">
              {/* 🔥 Owner canon Oct 2026 — single unified entry. "Fly Solo"
                  and "Open a Party Room" collapsed into one flow: the run
                  always opens as a party room; if no helpers join, it fires
                  solo automatically. */}
              <button
                onClick={createRoom}
                disabled={starting}
                data-testid="rush-start-btn"
                className={`py-3 rounded-lg text-xs font-black uppercase tracking-widest transition ${
                  starting
                    ? "bg-neutral-900 text-neutral-600 cursor-wait"
                    : "bg-gradient-to-r from-amber-600 to-red-600 text-white hover:brightness-125 shadow-lg shadow-amber-900/30"
                }`}
              >
                {starting ? "Opening the gate…" : "🔥 Open the gate"}
              </button>
            </div>

            {/* Lobby — rooms open to join */}
            <div className="mt-5 pt-4 border-t border-neutral-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">
                  Rooms open · tap to join
                </div>
                <button
                  onClick={loadLobby}
                  data-testid="rush-lobby-refresh"
                  className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-neutral-300"
                >
                  ↻ Refresh
                </button>
              </div>
              {lobby.length === 0 ? (
                <div className="text-[11px] text-neutral-600 italic py-2" data-testid="rush-lobby-empty">
                  No open rooms right now.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {lobby.map((s) => (
                    <button
                      key={s.session_id}
                      onClick={() => joinRoom(s.session_id)}
                      data-testid={`rush-lobby-row-${s.session_id}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded border border-neutral-800 bg-black/40 hover:border-cyan-500/60 hover:bg-cyan-950/30 transition text-left"
                    >
                      <div>
                        <div className="text-sm font-bold text-white">{s.host_handle}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {(s.helpers || []).length}/3 helpers
                        </div>
                      </div>
                      <span className="text-cyan-300 text-xs font-bold">JOIN →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        ) : (
          <>
            {/* Party roster (when 2+ players) */}
            {(isHost || isHelper) && (
              <section
                className="rounded-xl border border-cyan-500/40 bg-gradient-to-br from-cyan-950/30 via-black to-sky-950/20 p-4"
                data-testid="rush-party-roster"
              >
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-cyan-200">
                    🤝 Rush Party · {isHost ? "You are hosting" : `Joined ${sess.host_handle}`}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Both host and helpers tick to want rewards · unchecked = pure helping
                  </div>
                </div>
                {/* 🔥 Owner canon Oct 2026 — EVERY participant (host +
                    helpers) opts into rewards via checkbox. Unchecked =
                    pure helping, no boxes in your inventory. */}
                {isHost && (
                  <label
                    className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-amber-300 cursor-pointer"
                    data-testid="rush-host-wants-rewards-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="rush-host-wants-rewards"
                      checked={!!wantsRewards}
                      onChange={(e) => toggleHostRewards(e.target.checked)}
                      className="accent-amber-400"
                    />
                    I want rewards from this run (otherwise, pure hosting)
                  </label>
                )}
                {isHelper && (
                  <label
                    className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-cyan-300 cursor-pointer"
                    data-testid="rush-helper-wants-rewards-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="rush-helper-wants-rewards"
                      checked={!!wantsRewards}
                      onChange={(e) => toggleRewards(e.target.checked)}
                      className="accent-cyan-400"
                    />
                    I want rewards from this run (otherwise, pure helping)
                  </label>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="rounded-lg border border-cyan-500/40 bg-cyan-950/30 p-2 text-center">
                    <div className="text-xl">👑</div>
                    <div className="text-[11px] font-bold text-cyan-100 truncate">{sess.host_handle}</div>
                    <div className="text-[9px] uppercase tracking-widest text-cyan-400/80">Host</div>
                  </div>
                  {(sess.helpers || []).map((h, i) => (
                    <div key={h.user_id} className="rounded-lg border border-sky-500/30 bg-sky-950/30 p-2 text-center relative" data-testid={`rush-party-helper-${i}`}>
                      <div className="text-xl">🧑‍🤝‍🧑</div>
                      <div className="text-[11px] font-bold text-sky-100 truncate">{h.handle}</div>
                      <div className="text-[9px] uppercase tracking-widest text-sky-400/80">Helper</div>
                      {isHost && (
                        <button
                          onClick={() => kickHelper(h.user_id)}
                          data-testid={`rush-party-kick-${i}`}
                          title="Remove helper"
                          className="absolute top-1 right-1 text-red-400 hover:text-red-200 text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {Array.from({ length: Math.max(0, 3 - (sess.helpers || []).length) }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="rounded-lg border border-dashed border-neutral-700 bg-black/30 p-2 text-center"
                      data-testid={`rush-party-slot-empty-${i}`}
                    >
                      <div className="text-xl text-neutral-700">✧</div>
                      <div className="text-[11px] text-neutral-600 italic">Open slot</div>
                    </div>
                  ))}
                </div>
                {isHelper && (
                  <div className="mt-2 text-[11px] text-sky-300 italic" data-testid="rush-helper-note">
                    Standing by — only the host can fire the fight.
                  </div>
                )}
              </section>
            )}

            {/* Current boss showcase */}
            <section
              className="rounded-xl border-2 p-5"
              style={{
                borderColor: `${boss?.theme?.primary || "#F59E0B"}99`,
                background: `linear-gradient(135deg, ${boss?.theme?.primary || "#F59E0B"}1A, transparent 60%)`,
                boxShadow: `0 0 48px ${boss?.theme?.primary || "#F59E0B"}44`,
              }}
              data-testid="rush-current-boss"
            >
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-7xl" data-testid="rush-boss-icon">{boss?.icon || "👑"}</div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">
                    Boss · Lv{boss?.level}
                  </div>
                  <h2 className="text-2xl font-black text-white uppercase tracking-wider" data-testid="rush-boss-name">
                    {boss?.name}
                  </h2>
                  <p className="text-xs text-neutral-400 italic mt-1 max-w-xl" data-testid="rush-boss-lore">
                    {boss?.lore}
                  </p>
                  {boss?.has_passive && (
                    <div className="mt-1 inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-red-900/40 text-red-200 border border-red-500/40">
                      Passive Kit · beware
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <HPBar cur={sess.current_boss_hp} max={boss?.max_hp || 1} color={boss?.theme?.primary || "#DC2626"} label="Boss HP" />
                <HPBar cur={sess.party_hp} max={sess.party_hp_max || 1} color="#10B981" label="Party HP (combined)" />
              </div>

              {/* 👥 Feb 2026 — per-HERO HP bars (owner directive: each
                  hero has their own HP + attacks on their own). The
                  combined bar above is the sum, these are the real
                  per-combatant lifelines read from `sess.combatants`. */}
              <PartyHpCards sess={sess} />

              {/* 💸 Prep-Room Rush Kit Shop (owner canon 2026-10-05).
                  Buyable charges before (and still during) the run.
                  Hidden for helpers — only the host taps Fight, but
                  every participant can top up their own kit via the
                  party path. */}
              <div className="mt-3">
                <RushSkillShop
                  session={sess}
                  myUserId={myUserId}
                  onBuy={buyRushSkillCharge}
                  isPrep={isPrep}
                />
              </div>
              {sess.eviction_turns_left > 0 && (
                <div className="mt-2 text-[11px] text-red-300 font-bold" data-testid="rush-eviction">
                  🚪 Eviction active — one hero is sidelined ({sess.eviction_turns_left} turns left).
                </div>
              )}
              {(sess.adds || []).length > 0 && (
                <div className="mt-2 text-[11px] text-fuchsia-300 font-bold" data-testid="rush-adds">
                  👯 {(sess.adds || []).length} Latoyas standing — clear them to shut down Ocean.
                </div>
              )}

              <div className="mt-4 flex gap-2 flex-wrap">
                {sessStatus === "active" ? (
                  isHelper ? (
                    <div className="flex-1 py-3 rounded-lg bg-sky-950/40 border border-sky-500/40 text-sky-200 text-center text-xs uppercase tracking-widest" data-testid="rush-helper-wait">
                      ⏳ Host fires the fight — {wantsRewards ? "you'll share the rewards on every kill" : "you're pure helping (tick 'I want rewards' to share loot)"}.
                    </div>
                  ) : (
                    <button
                      onClick={fight}
                      disabled={fighting}
                      data-testid="rush-fight-round-btn"
                      className={`flex-1 py-3 rounded-lg text-xs font-black uppercase tracking-widest transition ${
                        fighting
                          ? "bg-neutral-900 text-neutral-600 cursor-wait"
                          : "bg-gradient-to-r from-red-600 to-amber-600 text-white hover:brightness-125"
                      }`}
                    >
                      {fighting ? "Rolling the round…" : (isPrep ? "⚔️ Start Fight" : "Fight the Round")}
                    </button>
                  )
                ) : (
                  <div className="flex-1 py-3 rounded-lg bg-neutral-900 text-neutral-500 text-center text-xs uppercase tracking-widest" data-testid="rush-finished">
                    {sessStatus === "complete" ? "🎆 Run complete" : "💀 Party wiped — the Rush is over"}
                  </div>
                )}
                {/* 🚫 Roc rule 2026-10-04 — Phoenix Rush is wipe-or-glory.
                    The Abandon button is intentionally gone. Helpers can
                    still leave the room (they're not the owner). */}
                {isHelper && (
                  <button
                    onClick={leaveRoom}
                    data-testid="rush-leave-room-btn"
                    className="px-4 py-3 rounded-lg bg-neutral-900 border border-neutral-700 text-neutral-300 text-xs uppercase tracking-widest hover:bg-neutral-800"
                  >
                    Leave Room
                  </button>
                )}
              </div>
            </section>

            {/* ⚡ Rush Special Skills — Flame Rage / Fortress / Respite / Phoenix Revival */}
            <RushSkillsBar
              session={sess}
              myUserId={myUserId}
              onUse={useRushSkill}
            />

            {/* Battle log */}
            <section
              className="rounded-xl border border-neutral-800 bg-black/60 p-4"
              data-testid="rush-log"
            >
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-bold">
                Battle log
              </div>
              <div
                ref={logBottomRef}
                className="space-y-1 max-h-64 overflow-y-auto text-sm pr-1"
              >
                {(sess.log || []).map((l, i) => {
                  const toneClass =
                    l.actor === "boss" ? "text-amber-200"
                    : l.actor === "dmg" ? "text-red-300"
                    : l.actor === "party" ? "text-emerald-300"
                    : "text-neutral-400 italic";
                  return (
                    <div key={i} data-testid={`rush-log-line-${i}`} className={`flex gap-2 ${toneClass}`}>
                      <span className="w-5 text-center">{l.icon}</span>
                      <span className="flex-1 leading-snug">{l.msg}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>

      <RushBoxOpenedModal
        payload={lastOpened}
        onClose={() => setLastOpened(null)}
      />
    </div>
  );
}
